import { Suspense, lazy, useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  auroraPalettes,
  cn,
  usePalette,
  type AuroraPaletteId,
} from '@ownyou/ui-design-system';
import { Badge } from '@ownyou/ui-components';
import { useAuth } from '../contexts/AuthContext';
import { useDataSource, type DataSourceId } from '../contexts/DataSourceContext';
import { useGDPR } from '../hooks/useGDPR';
import { getPlatform } from '../utils/platform';
import { startTauriOAuth, initOAuthListener } from '../utils/tauri-oauth';
import { BUILD_INFO } from '../build-info';
import {
  MODEL_ROUTE_DEFINITIONS,
  type LlmProviderId,
  type ModelRouteId,
} from '../lib/llm-routing';
import { useModelPreferences } from '../hooks/useModelPreferences';
import { useNotifications } from '../hooks/useNotifications';
import { getAgentKeyStatus } from '../utils/api-key-status';
import {
  DEFAULT_REFRESH_CONFIG,
  CATEGORY_LABELS,
  TTL_OPTIONS,
  DAY,
  getEffectiveRefreshConfig,
  saveCategoryTTLOverrides,
} from '../config/refresh-config';
import { formatTimeAgo as formatRelativeTime } from '@/utils/format-time';
import { setDisclosurePreference } from '../services/extension-credential-sync';
import { SettingsDisclosureSection } from '../components/settings/SettingsDisclosureSection';
import { SettingsTrustSection } from '../components/settings/SettingsTrustSection';
import { SettingsAdvancedAiRoutingSection } from '../components/settings/SettingsAdvancedAiRoutingSection';
import { buildGoogleWorkerOAuthUrl } from '../lib/oauth-integration';
import { loadPublicRuntimeConfig } from '../config/runtime-public-config';

const HITL_QA_TOOLS_ENABLED = import.meta.env.VITE_ENABLE_HITL_QA_TOOLS === 'true';

const HitlQaToolsPanel = HITL_QA_TOOLS_ENABLED
  ? lazy(() => import('../components/settings/HitlQaToolsPanel').then(module => ({
    default: module.HitlQaToolsPanel,
  })))
  : null;

/* ---------------------------------------------------------------------------
 * Data source definitions (no hardcoded connection state)
 * Connection status derived at render time from DataSourceContext.
 * -------------------------------------------------------------------------*/

interface SourceDefinition {
  id: string;
  name: string;
  type: 'oauth' | 'plaid';
}

const DATA_SOURCES: SourceDefinition[] = [
  { id: 'gmail', name: 'Gmail', type: 'oauth' },
  { id: 'google-calendar', name: 'Google Calendar', type: 'oauth' },
  { id: 'plaid', name: 'Financial (Plaid)', type: 'plaid' },
  { id: 'outlook', name: 'Outlook', type: 'oauth' },
  { id: 'microsoft-calendar', name: 'Microsoft Calendar', type: 'oauth' },
];

interface SourceDisplay {
  id: string;
  name: string;
  connected: boolean;
  lastSync: string | null;
  items: number;
}

interface PrivacyToggle {
  id: string;
  label: string;
  description: string;
  defaultEnabled: boolean;
}

const PRIVACY_TOGGLES: PrivacyToggle[] = [
  { id: 'iab', label: 'Allow IAB classification', description: 'Classify your data into interest categories', defaultEnabled: true },
  { id: 'ikigai', label: 'Allow Ikigai analysis', description: 'Analyze your data to build your Ikigai profile', defaultEnabled: true },
  { id: 'location', label: 'Allow location-based recommendations', description: 'Use your location for nearby restaurant and activity suggestions', defaultEnabled: false },
  { id: 'bbs', label: 'Allow anonymous data sharing (BBS+)', description: 'Share anonymized interest data with publishers to earn rewards', defaultEnabled: false },
  { id: 'filterNewsletters', label: 'Filter newsletters from analysis', description: 'Ignore bulk newsletter emails so high-intent signals (bookings, purchases, events) drive your missions', defaultEnabled: false },
];

/* ---------------------------------------------------------------------------
 * Collapsible section helper
 * -------------------------------------------------------------------------*/

interface CollapsibleSectionProps {
  title: string;
  testId: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function CollapsibleSection({ title, testId, defaultOpen = false, children }: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section data-testid={testId} className="border border-neutral-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'w-full flex items-center justify-between px-4 py-3 bg-neutral-50 hover:bg-neutral-100 transition-colors',
        )}
      >
        <h2 className="text-lg font-semibold text-neutral-900">{title}</h2>
        <svg
          className={cn('w-5 h-5 text-neutral-500 transition-transform', open && 'rotate-180')}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="px-4 py-4 space-y-4">{children}</div>}
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * OAuth helpers (preserved from Sprint 11)
 * -------------------------------------------------------------------------*/

/**
 * Generate PKCE code verifier and challenge for OAuth 2.0 PKCE flow.
 */
async function generatePKCE(): Promise<{ codeVerifier: string; codeChallenge: string }> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const codeVerifier = btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
  const codeChallenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  return { codeVerifier, codeChallenge };
}

function isLocalHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

export function resolveOAuthRedirectUri(
  configuredRedirectUri: string | undefined,
  fallbackPath: string,
  currentOrigin = window.location.origin,
): string {
  const fallback = `${currentOrigin}${fallbackPath}`;
  if (!configuredRedirectUri) return fallback;

  try {
    const current = new URL(currentOrigin);
    const configured = new URL(configuredRedirectUri, currentOrigin);

    if (isLocalHostname(current.hostname) || configured.origin === current.origin) {
      return configured.toString();
    }
  } catch {
    return fallback;
  }

  return fallback;
}

export function resolveGoogleOAuthRedirectUri(
  configuredRedirectUri: string | undefined,
  currentOrigin = window.location.origin,
): string {
  const callbackPath = '/oauth/gmail/callback';
  const fallback = `${currentOrigin}${callbackPath}`;
  if (!configuredRedirectUri) return fallback;

  try {
    const current = new URL(currentOrigin);
    const configured = new URL(configuredRedirectUri, currentOrigin);

    if (isLocalHostname(current.hostname)) {
      return configured.toString();
    }

    if (configured.origin === current.origin && configured.pathname === callbackPath) {
      return configured.toString();
    }
  } catch {
    return fallback;
  }

  return fallback;
}

/**
 * Build OAuth authorization URL for a given data source.
 */
async function getOAuthUrl(sourceId: DataSourceId): Promise<string | null> {
  const runtimeConfig = await loadPublicRuntimeConfig();
  const microsoftClientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID || runtimeConfig.microsoftClientId;
  const microsoftRedirectUri = encodeURIComponent(
    resolveOAuthRedirectUri(
      import.meta.env.VITE_MICROSOFT_REDIRECT_URI || runtimeConfig.microsoftRedirectUri,
      '/auth/callback',
    ),
  );

  if (sourceId === 'gmail') {
    return buildGoogleWorkerOAuthUrl('gmail');
  }
  if (sourceId === 'outlook') {
    const { codeVerifier, codeChallenge } = await generatePKCE();
    localStorage.setItem(`oauth_code_verifier_${sourceId}`, codeVerifier);
    const clientId = microsoftClientId;
    if (!clientId) return null;
    const scopes = encodeURIComponent('https://graph.microsoft.com/Mail.Read offline_access');
    return `https://login.microsoftonline.com/consumers/oauth2/v2.0/authorize?client_id=${clientId}&redirect_uri=${microsoftRedirectUri}&response_type=code&scope=${scopes}&state=outlook&code_challenge=${codeChallenge}&code_challenge_method=S256`;
  }
  if (sourceId === 'google-calendar') {
    return buildGoogleWorkerOAuthUrl('google-calendar');
  }
  if (sourceId === 'microsoft-calendar') {
    const { codeVerifier, codeChallenge } = await generatePKCE();
    localStorage.setItem(`oauth_code_verifier_${sourceId}`, codeVerifier);
    const clientId = microsoftClientId;
    if (!clientId) return null;
    const scopes = encodeURIComponent('https://graph.microsoft.com/Calendars.Read offline_access');
    return `https://login.microsoftonline.com/consumers/oauth2/v2.0/authorize?client_id=${clientId}&redirect_uri=${microsoftRedirectUri}&response_type=code&scope=${scopes}&state=microsoft-calendar&code_challenge=${codeChallenge}&code_challenge_method=S256`;
  }
  return null;
}

/**
 * Open OAuth popup and wait for token callback.
 *
 * RC3 (2026-05-19): returns the FULL token payload (accessToken + refreshToken +
 * expiresAt) the popup receives, not just the access token. OAuthCallback now
 * postMessages the full payload; consuming only the access token re-introduces
 * the false-positive Connected → Sync → Connect cycle because the resulting
 * connection has no refresh path.
 */
interface PopupOAuthResult {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number;
  provider: 'google' | 'microsoft';
}

async function openOAuthPopup(oauthUrl: string, sourceId: DataSourceId): Promise<PopupOAuthResult | null> {
  return new Promise((resolve) => {
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      oauthUrl,
      `oauth_${sourceId}`,
      `width=${width},height=${height},left=${left},top=${top},popup=yes`,
    );

    if (!popup) {
      resolve(null);
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      const allowedOrigins = new Set([
        window.location.origin,
        'https://ownyou-oauth-callback.nlongcroft.workers.dev',
      ]);
      if (!allowedOrigins.has(event.origin)) return;

      if (event.data?.type === 'oauth_callback' && event.data?.sourceId === sourceId) {
        window.removeEventListener('message', handleMessage);
        popup.close();

        const expectedState = localStorage.getItem(`oauth_worker_state_${sourceId}`);
        if (expectedState) {
          localStorage.removeItem(`oauth_worker_state_${sourceId}`);
          if (event.data.state !== expectedState) {
            resolve(null);
            return;
          }
        }

        if (!event.data.accessToken) {
          resolve(null);
          return;
        }
        const isGoogle = sourceId === 'gmail' || sourceId === 'google-calendar';
        resolve({
          accessToken: event.data.accessToken,
          refreshToken: event.data.refreshToken,
          expiresAt: event.data.expiresAt,
          provider: event.data.provider ?? (isGoogle ? 'google' : 'microsoft'),
        });
      }
    };

    window.addEventListener('message', handleMessage);

    const pollTimer = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollTimer);
        window.removeEventListener('message', handleMessage);
        resolve(null);
      }
    }, 500);

    setTimeout(() => {
      clearInterval(pollTimer);
      window.removeEventListener('message', handleMessage);
      if (!popup.closed) popup.close();
      resolve(null);
    }, 5 * 60 * 1000);
  });
}

/* ---------------------------------------------------------------------------
 * Main Settings component
 * -------------------------------------------------------------------------*/

export function Settings() {
  const hitlQaToolsEnabled = HITL_QA_TOOLS_ENABLED;
  const { paletteId, setPalette } = usePalette();
  const { isAuthenticated, disconnect } = useAuth();
  const { dataSources, connectSource, syncSource } = useDataSource();
  const { downloadExport, deleteAllDataAndLogout, isExporting, isDeleting, isReady: gdprReady } = useGDPR();
  const {
    models,
    routeSelections,
    setRouteSelection,
    clearRouteSelection,
    isLoading: modelsLoading,
    refreshModels,
  } = useModelPreferences();
  const navigate = useNavigate();

  // Track selected provider per explicit operation route for the two-dropdown pattern.
  const [routeProviders, setRouteProviders] = useState<Record<ModelRouteId, LlmProviderId | ''>>(
    () => {
      const init = {} as Record<ModelRouteId, LlmProviderId | ''>;
      for (const route of MODEL_ROUTE_DEFINITIONS) {
        init[route.id] = routeSelections[route.id]?.provider ?? '';
      }
      return init;
    },
  );

  // Sync provider controls from saved selections only; catalog refreshes should not
  // reset a user's explicit per-route provider dropdown state.
  useEffect(() => {
    setRouteProviders(prev => {
      const next = { ...prev };
      let changed = false;
      for (const route of MODEL_ROUTE_DEFINITIONS) {
        const provider = routeSelections[route.id]?.provider ?? '';
        if (next[route.id] !== provider) {
          next[route.id] = provider;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [routeSelections]);

  // Privacy toggle state — persisted to localStorage
  const [privacyState, setPrivacyState] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('ownyou_privacy_toggles');
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    const initial: Record<string, boolean> = {};
    for (const t of PRIVACY_TOGGLES) {
      initial[t.id] = t.defaultEnabled;
    }
    return initial;
  });

  // Persist privacy toggles. Routed through `setDisclosurePreference` so the
  // same-tab credential-sync listener fires `ownyou:preference-changed` and
  // re-pushes credentials to the extension. Plain `localStorage.setItem` only
  // notifies OTHER tabs/windows — see sixth-pass review M1.
  useEffect(() => {
    setDisclosurePreference('ownyou_privacy_toggles', JSON.stringify(privacyState));
  }, [privacyState]);

  // Agent API key status for Developer section
  const keyStatus = useMemo(() => getAgentKeyStatus(), []);
  const [showHitlQaTools, setShowHitlQaTools] = useState(false);

  // Notification state — persisted via NotificationService
  const { settings: notifSettings, updateSettings: updateNotifSettings } = useNotifications();
  const [notifFrequency, setNotifFrequency] = useState('normal');
  const notifEnabled = notifSettings.enabled;
  const setNotifEnabled = useCallback((enabled: boolean) => {
    updateNotifSettings({ ...notifSettings, enabled });
  }, [notifSettings, updateNotifSettings]);

  // Mission TTL state — persisted to localStorage
  const [categoryTTLs, setCategoryTTLs] = useState<Record<string, number>>(() => {
    const effective = getEffectiveRefreshConfig();
    return { ...effective.categoryTTL };
  });

  const handleTTLChange = useCallback((category: string, value: number) => {
    setCategoryTTLs(prev => {
      const next = { ...prev, [category]: value };
      // Save only overrides that differ from defaults
      const overrides: Record<string, number> = {};
      for (const [cat, ttl] of Object.entries(next)) {
        if (ttl !== DEFAULT_REFRESH_CONFIG.categoryTTL[cat]) {
          overrides[cat] = ttl;
        }
      }
      saveCategoryTTLOverrides(overrides);
      return next;
    });
  }, []);

  const resetTTLsToDefaults = useCallback(() => {
    setCategoryTTLs({ ...DEFAULT_REFRESH_CONFIG.categoryTTL });
    saveCategoryTTLOverrides({});
  }, []);

  // OAuth state
  const [isConnecting, setIsConnecting] = useState<DataSourceId | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Init Tauri OAuth listener on mount
  useEffect(() => {
    if (getPlatform() === 'tauri') {
      initOAuthListener();
    }
  }, []);

  // Check for pending OAuth token from redirect flow.
  //
  // RC3 (2026-05-19): only the `oauth_token_data` path is honoured. The legacy
  // `oauth_token` (access-token-only) branch was removed — it fabricated a
  // tokenData object without refreshToken/expiresAt, which RC2's validation
  // can't refresh and Sync Now can't survive, producing the false-positive
  // Connected → Sync → Connect cycle. OAuthCallback now always writes the
  // full token payload to `oauth_token_data`. Any stale `oauth_token` from a
  // previous build is cleared and ignored.
  useEffect(() => {
    const pendingTokenDataStr = sessionStorage.getItem('oauth_token_data');
    const pendingProvider = sessionStorage.getItem('oauth_provider') as DataSourceId | null;

    // Drop any stale legacy key.
    sessionStorage.removeItem('oauth_token');

    if (pendingTokenDataStr && pendingProvider) {
      sessionStorage.removeItem('oauth_token_data');
      sessionStorage.removeItem('oauth_provider');
      try {
        const tokenData = JSON.parse(pendingTokenDataStr);
        setIsConnecting(pendingProvider);
        connectSource(pendingProvider, tokenData).finally(() => setIsConnecting(null));
      } catch {
        // silently ignore parse failures
      }
    }
  }, [connectSource]);

  /** Perform OAuth connect (Tauri deep link or PWA popup). */
  const performOAuthConnect = useCallback(
    async (sourceId: DataSourceId) => {
      const platform = getPlatform();
      setIsConnecting(sourceId);
      try {
        if (platform === 'tauri') {
          const tokenData = await startTauriOAuth(sourceId);
          if (tokenData) {
            await connectSource(sourceId, tokenData);
          }
        } else {
          const oauthUrl = await getOAuthUrl(sourceId);
          if (!oauthUrl) {
            setIsConnecting(null);
            return;
          }
          const popupResult = await openOAuthPopup(oauthUrl, sourceId);
          if (popupResult) {
            await connectSource(sourceId, {
              accessToken: popupResult.accessToken,
              refreshToken: popupResult.refreshToken,
              expiresAt: popupResult.expiresAt,
              provider: popupResult.provider,
            });
          }
        }
      } catch (error) {
        console.error(`[Settings] OAuth error for ${sourceId}:`, error);
      } finally {
        setIsConnecting(null);
      }
    },
    [connectSource],
  );

  const handleConnect = useCallback(
    async (sourceId: DataSourceId) => {
      await performOAuthConnect(sourceId);
    },
    [performOAuthConnect],
  );

  const handleSync = useCallback(
    async (sourceId: DataSourceId) => {
      try {
        await syncSource(sourceId);
      } catch (error) {
        console.error(`[Settings] Sync error for ${sourceId}:`, error);
      }
    },
    [syncSource],
  );

  const handleSignOut = useCallback(async () => {
    try {
      await disconnect();
      navigate('/');
    } catch (error) {
      console.error('[Settings] Sign out error:', error);
    }
  }, [disconnect, navigate]);

  // Build display sources from DATA_SOURCES definitions + real DataSourceContext state
  const displaySources: SourceDisplay[] = DATA_SOURCES.map((def) => {
    const real = dataSources.find((ds) => ds.id === def.id);
    return {
      id: def.id,
      name: def.name,
      connected: real?.status === 'connected',
      lastSync: real?.lastSync ? formatRelativeTime(real.lastSync) : null,
      items: real?.itemCount ?? 0,
    };
  });

  return (
    <div data-testid="settings-screen" className="flex flex-col min-h-screen pb-32 px-4">
      <h1 className="text-2xl font-bold text-neutral-900 py-6">Settings</h1>
      <p className="-mt-4 mb-5 text-sm text-neutral-500">
        Trust controls for identity, disclosure, routing, AuditLog, and data rights.
      </p>

      <div className="space-y-4">
        {/* 1. Account */}
        <CollapsibleSection title="Account" testId="account-section" defaultOpen>
          <p className="text-sm text-neutral-600">
            {isAuthenticated ? 'Logged in via Google' : 'Not logged in'}
          </p>
          <button
            data-testid="sign-out-button"
            onClick={handleSignOut}
            className={cn(
              'mt-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
            )}
          >
            Sign Out
          </button>
        </CollapsibleSection>

        <CollapsibleSection title="Trust" testId="trust-section" defaultOpen>
          <SettingsTrustSection />
        </CollapsibleSection>

        <CollapsibleSection title="Palette" testId="palette-section" defaultOpen>
          <div data-testid="palette-picker" className="grid grid-cols-1 gap-3 sm:grid-cols-5">
            {(Object.keys(auroraPalettes) as AuroraPaletteId[]).map((id) => {
              const palette = auroraPalettes[id];
              const active = id === paletteId;
              return (
                <button
                  key={id}
                  type="button"
                  data-testid={`palette-option-${id}`}
                  aria-pressed={active}
                  onClick={() => setPalette(id)}
                  className="rounded-xl border p-3 text-left transition-transform hover:-translate-y-0.5"
                  style={{
                    borderColor: active ? 'var(--aurora-accent)' : 'var(--aurora-ink-line)',
                    background: active ? 'var(--aurora-card-solid)' : 'var(--aurora-card)',
                    boxShadow: active ? 'var(--aurora-card-shadow)' : undefined,
                  }}
                >
                  <span className="block text-sm font-medium" style={{ color: 'var(--aurora-ink)' }}>
                    {palette.label}
                  </span>
                  <span className="mt-3 flex gap-1">
                    {palette.swatch.map((color) => (
                      <span
                        key={color}
                        aria-hidden="true"
                        className="h-5 flex-1 rounded-full"
                        style={{ background: color }}
                      />
                    ))}
                  </span>
                </button>
              );
            })}
          </div>
        </CollapsibleSection>

        {/* 2. Data Sources */}
        <CollapsibleSection title="Data Sources" testId="data-sources-settings" defaultOpen>
          <div className="space-y-3">
            {displaySources.map((source) => {
              const isThisConnecting = isConnecting === source.id;
              return (
                <div
                  key={source.id}
                  data-testid={`settings-source-${source.id}`}
                  className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-neutral-900">{source.name}</span>
                    <div className="mt-1">
                      <Badge variant={source.connected ? 'success' : 'default'}>
                        {source.connected ? 'Connected' : 'Not Connected'}
                      </Badge>
                    </div>
                    {source.connected && source.lastSync && (
                      <span className="text-xs text-neutral-400 block mt-1">
                        Last sync: {source.lastSync}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() =>
                      source.connected
                        ? handleSync(source.id as DataSourceId)
                        : handleConnect(source.id as DataSourceId)
                    }
                    disabled={isThisConnecting}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50',
                      source.connected
                        ? 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                        : 'bg-primary-500 text-white hover:bg-primary-600',
                    )}
                  >
                    {isThisConnecting
                      ? 'Connecting...'
                      : source.connected
                        ? 'Sync Now'
                        : 'Connect'}
                  </button>
                </div>
              );
            })}
            <button
              onClick={() => navigate('/connections')}
              data-testid="manage-connections-link"
              className="w-full mt-2 px-3 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors text-center"
            >
              Manage all connections
            </button>
          </div>
        </CollapsibleSection>

        {/* AT-13 — Disclosure (read-only viewer; editable controls in Slice 3) */}
        <CollapsibleSection title="Disclosure" testId="disclosure-section" defaultOpen>
          <SettingsDisclosureSection />
        </CollapsibleSection>

        {/* 3. Privacy */}
        <CollapsibleSection title="Privacy" testId="privacy-section" defaultOpen>
          <div className="space-y-4">
            {PRIVACY_TOGGLES.map((toggle) => (
              <div
                key={toggle.id}
                data-testid={`toggle-${toggle.id}`}
                className="flex items-center justify-between gap-4"
              >
                <div className="flex-1 min-w-0">
                  <label className="font-medium text-neutral-900 text-sm">{toggle.label}</label>
                  <p className="text-xs text-neutral-500 mt-0.5">{toggle.description}</p>
                </div>
                <input
                  type="checkbox"
                  checked={privacyState[toggle.id] ?? false}
                  onChange={() =>
                    setPrivacyState((prev) => ({ ...prev, [toggle.id]: !prev[toggle.id] }))
                  }
                  className="w-5 h-5 rounded text-primary-500 focus:ring-primary-500"
                />
              </div>
            ))}
          </div>
        </CollapsibleSection>

        {/* 4. Advanced AI Routing */}
        <CollapsibleSection
          title="Advanced AI Routing"
          testId="ai-models-section"
          defaultOpen={MODEL_ROUTE_DEFINITIONS.some((route) => !routeSelections[route.id])}
        >
          <SettingsAdvancedAiRoutingSection
            models={models}
            routeSelections={routeSelections}
            routeProviders={routeProviders}
            setRouteProviders={setRouteProviders}
            setRouteSelection={setRouteSelection}
            clearRouteSelection={clearRouteSelection}
            modelsLoading={modelsLoading}
            refreshModels={refreshModels}
          />
        </CollapsibleSection>

        {/* 5. Mission Card Lifetimes */}
        <CollapsibleSection title="Mission Card Lifetimes" testId="mission-ttl-section">
          <p className="text-xs text-neutral-500 mb-3">
            How long mission cards are kept before being cleaned up. Longer lifetimes mean more cards in your feed, but they may become less relevant over time.
          </p>
          <div className="space-y-3">
            {Object.keys(DEFAULT_REFRESH_CONFIG.categoryTTL).map(category => (
              <div
                key={category}
                className="flex items-center justify-between gap-3"
                data-testid={`ttl-${category}`}
              >
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-neutral-900">
                    {CATEGORY_LABELS[category] ?? category}
                  </span>
                  <span className="text-xs text-neutral-400 ml-2">
                    {Math.round((categoryTTLs[category] ?? 0) / DAY)}d
                  </span>
                </div>
                <select
                  value={categoryTTLs[category] ?? DEFAULT_REFRESH_CONFIG.categoryTTL[category]}
                  onChange={(e) => handleTTLChange(category, Number(e.target.value))}
                  className="w-28 p-1.5 border border-neutral-200 rounded-lg text-sm"
                  data-testid={`ttl-select-${category}`}
                >
                  {TTL_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <button
            onClick={resetTTLsToDefaults}
            className="mt-3 px-3 py-1.5 text-xs font-medium text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
            data-testid="reset-ttl-defaults"
          >
            Reset to defaults
          </button>
        </CollapsibleSection>

        {/* 6. Notifications — persisted via NotificationService */}
        <CollapsibleSection title="Notifications" testId="notifications-section">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-neutral-700">Notifications enabled</span>
              <input
                type="checkbox"
                data-testid="notifications-enabled-toggle"
                checked={notifEnabled}
                onChange={(e) => setNotifEnabled(e.target.checked)}
                className="w-5 h-5 rounded text-primary-500 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 block mb-1">Frequency</label>
              <select
                data-testid="frequency-select"
                value={notifFrequency}
                onChange={(e) => setNotifFrequency(e.target.value)}
                className="w-full p-2 border border-neutral-200 rounded-lg text-sm"
              >
                <option value="aggressive">Aggressive</option>
                <option value="normal">Normal</option>
                <option value="quiet">Quiet</option>
              </select>
            </div>
          </div>
        </CollapsibleSection>

        {/* 5. About */}
        <CollapsibleSection title="About" testId="about-section">
          <p className="text-sm text-neutral-600">OwnYou v{BUILD_INFO.version}</p>
          <div className="mt-3 space-y-2">
            <a href="#" className="block text-sm text-primary-500 hover:underline">
              Privacy Policy
            </a>
            <a href="#" className="block text-sm text-primary-500 hover:underline">
              Terms of Service
            </a>
          </div>
        </CollapsibleSection>

        {/* 6. Developer */}
        <CollapsibleSection title="Developer" testId="developer-section" defaultOpen={false}>
          <div className="space-y-4">
            {/* API Key Status */}
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wide mb-2">Agent API Keys</p>
              <div className="space-y-1.5">
                {keyStatus.map(agent => (
                  <div key={agent.agent} className="flex justify-between text-xs" data-testid={`api-key-${agent.agent}`}>
                    <span className="text-neutral-700 capitalize">{agent.agent}</span>
                    <span className={agent.configured ? 'text-emerald-500' : 'text-amber-500'}>
                      {agent.configured ? 'Configured' : agent.fallback === 'llm' ? 'LLM fallback' : 'Missing'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            {hitlQaToolsEnabled && (
              <div className="border-t border-neutral-200 pt-3">
                <button
                  type="button"
                  data-testid="developer-show-hitl-tools"
                  onClick={() => setShowHitlQaTools((value) => !value)}
                  className="rounded-lg bg-neutral-900 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-neutral-700"
                >
                  {showHitlQaTools ? 'Hide HITL QA tools' : 'Show HITL QA tools'}
                </button>
                {showHitlQaTools && (
                  <div className="mt-3 space-y-3" data-testid="developer-hitl-tools">
                    {HitlQaToolsPanel && (
                      <Suspense fallback={<p className="text-xs text-neutral-500">Loading HITL QA tools...</p>}>
                        <HitlQaToolsPanel />
                      </Suspense>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </CollapsibleSection>

        {/* 7. Danger Zone */}
        <CollapsibleSection title="Danger Zone" testId="danger-zone">
          <div className="space-y-3">
            <button
              data-testid="export-data-button"
              onClick={async () => {
                try {
                  await downloadExport();
                } catch (err) {
                  console.error('[Settings] Export failed:', err);
                }
              }}
              disabled={!gdprReady || isExporting}
              className="w-full py-2.5 text-center text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors disabled:opacity-50"
            >
              {isExporting ? 'Exporting...' : 'Export All Data'}
            </button>

            {showDeleteConfirm ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700 font-medium mb-3">
                  This will permanently delete ALL your data. This action cannot be undone.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      try {
                        await deleteAllDataAndLogout();
                        navigate('/');
                      } catch {
                        // handled by hook
                      }
                    }}
                    disabled={isDeleting}
                    className="flex-1 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 disabled:opacity-50"
                  >
                    {isDeleting ? 'Deleting...' : 'Confirm Delete'}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 py-2 bg-neutral-200 text-neutral-700 text-sm rounded-lg hover:bg-neutral-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                data-testid="delete-data-button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={!gdprReady || isDeleting}
                className="w-full py-2.5 text-center text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
              >
                Delete All Data
              </button>
            )}

            <button
              data-testid="delete-account-button"
              onClick={async () => {
                if (window.confirm('Are you sure you want to delete your account? This is permanent.')) {
                  try {
                    await deleteAllDataAndLogout();
                    navigate('/');
                  } catch {
                    // handled by hook
                  }
                }
              }}
              disabled={!gdprReady || isDeleting}
              className="w-full py-2.5 text-center text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors disabled:opacity-50"
            >
              Delete Account
            </button>
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
}
